import { Link } from 'react-router-dom';
import {
  BadgeHelp,
  BookOpen,
  CheckCircle2,
  CreditCard,
  LayoutDashboard,
  Settings,
  Sparkles,
  Users,
  Wand2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Layout from '../components/Layout.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';

export default function Help() {
  const { t } = useAppTranslation();

  const onboardingSteps = [
    {
      title: t('help.steps.dashboard.title'),
      description: t('help.steps.dashboard.description'),
      icon: LayoutDashboard,
      marker: t('help.steps.dashboard.marker'),
    },
    {
      title: t('help.steps.users.title'),
      description: t('help.steps.users.description'),
      icon: Users,
      marker: t('help.steps.users.marker'),
    },
    {
      title: t('help.steps.cards.title'),
      description: t('help.steps.cards.description'),
      icon: CreditCard,
      marker: t('help.steps.cards.marker'),
    },
    {
      title: t('help.steps.settings.title'),
      description: t('help.steps.settings.description'),
      icon: Settings,
      marker: t('help.steps.settings.marker'),
    },
  ];

  const faqs = [
    {
      question: t('help.faq.start.question'),
      answer: t('help.faq.start.answer'),
    },
    {
      question: t('help.faq.bulk.question'),
      answer: t('help.faq.bulk.answer'),
    },
    {
      question: t('help.faq.settings.question'),
      answer: t('help.faq.settings.answer'),
    },
    {
      question: t('help.faq.cards.question'),
      answer: t('help.faq.cards.answer'),
    },
  ];

  const simplificationIdeas = [
    t('help.ideas.tour'),
    t('help.ideas.emptyStates'),
    t('help.ideas.inlineHelp'),
    t('help.ideas.templates'),
    t('help.ideas.checklist'),
    t('help.ideas.tooltips'),
  ];

  return (
    <Layout title={t('help.pageTitle')}>
      <div className="mx-auto max-w-6xl space-y-6">
        <section className="overflow-hidden rounded-[28px] border border-line bg-panel bg-[radial-gradient(circle_at_top_left,rgb(var(--accent-500)/0.16),transparent_45%)] p-6 sm:p-8">
          <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-accent-400/20 bg-accent-100 dark:bg-accent-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-accent-700 dark:text-accent-200">
                <BadgeHelp className="h-3.5 w-3.5" />
                {t('help.badge')}
              </div>
              <div className="space-y-3">
                <h2 className="text-3xl font-black tracking-tight text-ink sm:text-4xl">
                  {t('help.hero.title')}
                </h2>
                <p className="max-w-2xl text-sm leading-6 text-ink sm:text-base">
                  {t('help.hero.description')}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/users"
                  className="rounded-full bg-accent-600 px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                >
                  {t('help.hero.primaryAction')}
                </Link>
                <Link
                  to="/cards/new"
                  className="rounded-full border border-line bg-panel px-5 py-2.5 text-sm font-semibold text-ink transition-all hover:border-line-strong hover:bg-raised hover:text-ink"
                >
                  {t('help.hero.secondaryAction')}
                </Link>
              </div>
            </div>

            <div className="rounded-[24px] border border-line bg-panel/80 p-4 shadow-2xl shadow-black/30">
              <div className="mb-3 flex items-center gap-2 text-xs text-ink-muted">
                <span className="h-2.5 w-2.5 rounded-full bg-danger-400/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-warning-300/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-success-300/80" />
                <span className="ml-2">{t('help.hero.previewLabel')}</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <MiniShot
                  title={t('help.preview.dashboard.title')}
                  subtitle={t('help.preview.dashboard.subtitle')}
                  accent="bg-accent-500/30"
                  bars={[78, 56, 42]}
                />
                <MiniShot
                  title={t('help.preview.users.title')}
                  subtitle={t('help.preview.users.subtitle')}
                  accent="from-accent-500/25 to-accent-500/10"
                  bars={[72, 62, 48]}
                />
                <MiniShot
                  title={t('help.preview.cards.title')}
                  subtitle={t('help.preview.cards.subtitle')}
                  accent="from-accent-500/25 to-accent-500/10"
                  bars={[88, 50, 68]}
                />
                <MiniShot
                  title={t('help.preview.settings.title')}
                  subtitle={t('help.preview.settings.subtitle')}
                  accent="from-success-500/25 to-success-500/10"
                  bars={[54, 76, 34]}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <BookOpen className="h-5 w-5 text-accent-700 dark:text-accent-300" />
            <div>
              <h3 className="text-lg font-semibold text-ink">{t('help.quickstart.title')}</h3>
              <p className="text-sm text-ink-muted">
                {t('help.quickstart.description')}
              </p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {onboardingSteps.map((step, index) => (
              <StepCard
                key={step.title}
                title={step.title}
                description={step.description}
                marker={step.marker}
                tone={index}
                icon={step.icon}
              />
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-warning-700 dark:text-warning-300" />
            <div>
              <h3 className="text-lg font-semibold text-ink">{t('help.improvements.title')}</h3>
              <p className="text-sm text-ink-muted">
                {t('help.improvements.description')}
              </p>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {simplificationIdeas.map((idea) => (
              <div
                key={idea}
                className="rounded-2xl border border-line bg-panel p-4 text-sm leading-6 text-ink"
              >
                <div className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-xl bg-warning-100 dark:bg-warning-400/10 text-warning-700 dark:text-warning-300">
                  <Wand2 className="h-4 w-4" />
                </div>
                {idea}
              </div>
            ))}
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-2xl border border-line bg-panel p-5 sm:p-6">
            <div className="mb-4 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-success-700 dark:text-success-300" />
              <div>
                <h3 className="text-lg font-semibold text-ink">{t('help.faq.title')}</h3>
                <p className="text-sm text-ink-muted">{t('help.faq.description')}</p>
              </div>
            </div>

            <div className="space-y-3">
              {faqs.map((item) => (
                <details
                  key={item.question}
                  className="group rounded-2xl border border-line bg-panel px-4 py-3 open:border-accent-400/30 open:bg-accent-500/5"
                >
                  <summary className="cursor-pointer list-none text-sm font-semibold text-ink">
                    {item.question}
                  </summary>
                  <p className="mt-2 text-sm leading-6 text-ink-muted">{item.answer}</p>
                </details>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-line bg-raised p-5 sm:p-6">
            <h3 className="text-lg font-semibold text-ink">{t('help.next.title')}</h3>
            <p className="mt-2 text-sm leading-6 text-ink-muted">
              {t('help.next.description')}
            </p>
            <div className="mt-5 space-y-3">
              <QuickLink
                to="/"
                title={t('help.next.dashboard.title')}
                text={t('help.next.dashboard.text')}
              />
              <QuickLink
                to="/users"
                title={t('help.next.users.title')}
                text={t('help.next.users.text')}
              />
              <QuickLink
                to="/settings"
                title={t('help.next.settings.title')}
                text={t('help.next.settings.text')}
              />
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function StepCard({
  title,
  description,
  marker,
  tone,
  icon: Icon,
}: {
  title: string;
  description: string;
  marker: string;
  tone: number;
  icon: LucideIcon;
}) {
  const tones = [
    'bg-accent-500/20',
    'from-accent-500/20 to-accent-500/5',
    'from-accent-500/20 to-accent-500/5',
    'from-success-500/20 to-success-500/5',
  ];

  return (
    <article className="overflow-hidden rounded-[24px] border border-line bg-panel">
      <div className={`border-b border-line bg-gradient-to-br ${tones[tone] ?? tones[0]} p-4`}>
        <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-line bg-raised text-ink">
          <Icon className="h-5 w-5" />
        </div>
        <h4 className="text-base font-semibold text-ink">{title}</h4>
        <p className="mt-2 text-sm leading-6 text-ink">{description}</p>
      </div>

      <div className="p-4">
        <ScreenshotCard marker={marker} />
      </div>
    </article>
  );
}

function ScreenshotCard({ marker }: { marker: string }) {
  const { t } = useAppTranslation();

  return (
    <div className="rounded-[22px] border border-line bg-canvas p-3 shadow-inner shadow-black/30">
      <div className="mb-3 flex items-center gap-2 text-[11px] text-ink-faint">
        <span className="h-2 w-2 rounded-full bg-danger-300/80" />
        <span className="h-2 w-2 rounded-full bg-warning-300/80" />
        <span className="h-2 w-2 rounded-full bg-success-300/80" />
        <span className="ml-2">{t('help.screenshotPreview')}</span>
      </div>

      <div className="grid gap-3 md:grid-cols-[0.34fr_1fr]">
        <div className="rounded-2xl border border-line bg-panel p-2">
          <div className="space-y-2">
            {[0, 1, 2, 3].map((item) => (
              <div
                key={item}
                className={`h-8 rounded-xl ${item === 1 ? 'bg-accent-500/25 ring-1 ring-accent-400/30' : 'bg-white/5'}`}
              />
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-line bg-panel p-3">
          <div className="mb-3 h-9 rounded-xl bg-panel" />
          <div className="grid gap-3 sm:grid-cols-3">
            {[0, 1, 2].map((item) => (
              <div key={item} className="rounded-2xl border border-line bg-panel p-3">
                <div className="h-2.5 w-16 rounded-full bg-raised" />
                <div className="mt-3 h-8 w-12 rounded-lg bg-accent-400/25" />
                <div className="mt-3 h-2 w-full rounded-full bg-raised" />
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-2xl border border-dashed border-warning-300/30 bg-warning-300/5 px-4 py-3 text-xs text-warning-100/80">
            {t('help.highlightLabel')}: {marker}
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniShot({
  title,
  subtitle,
  accent,
  bars,
}: {
  title: string;
  subtitle: string;
  accent: string;
  bars: number[];
}) {
  return (
    <div className={`rounded-2xl border border-white/10 bg-gradient-to-br ${accent} p-3`}>
      <div className="mb-3">
        <p className="text-sm font-semibold text-ink">{title}</p>
        <p className="text-xs text-ink-muted">{subtitle}</p>
      </div>
      <div className="space-y-2">
        {bars.map((width) => (
          <div key={width} className="h-2 rounded-full bg-raised">
            <div className="h-2 rounded-full bg-white/60" style={{ width: `${width}%` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function QuickLink({ to, title, text }: { to: string; title: string; text: string }) {
  return (
    <Link
      to={to}
      className="block rounded-2xl border border-line bg-panel p-4 transition-all hover:border-line-strong hover:bg-raised"
    >
      <p className="text-sm font-semibold text-ink">{title}</p>
      <p className="mt-1 text-sm leading-6 text-ink-muted">{text}</p>
    </Link>
  );
}
